import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Format a date/time string to display with full time and timezone
 * @param dateString - ISO date string or Date object
 * @param use24Hour - Whether to use 24-hour format
 * @param timezone - Timezone to display (defaults to browser timezone)
 * @returns Formatted date time string (e.g., "Jul 19, 2025 11:59:30 PM (CST)")
 */
export function formatDateTime(
  dateString: string | Date,
  use24Hour = false,
  timezone?: string
): string {
  const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
  
  // Get the timezone to use (default to browser timezone)
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Get timezone abbreviation
  const tzAbbr = getTimezoneAbbreviation(date, tz);
  
  // Format the date/time
  const formatStr = use24Hour 
    ? 'MMM d, yyyy HH:mm:ss' 
    : 'MMM d, yyyy h:mm:ss a';
  
  const formatted = formatInTimeZone(date, tz, formatStr);
  
  return `${formatted} (${tzAbbr})`;
}

/**
 * Format time only with timezone
 * @param dateString - ISO date string or Date object
 * @param use24Hour - Whether to use 24-hour format
 * @param timezone - Timezone to display (defaults to browser timezone)
 * @returns Formatted time string (e.g., "11:59:30 PM (CST)")
 */
export function formatTime(
  dateString: string | Date,
  use24Hour = false,
  timezone?: string
): string {
  const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
  
  // Get the timezone to use (default to browser timezone)
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Get timezone abbreviation
  const tzAbbr = getTimezoneAbbreviation(date, tz);
  
  // Format the time
  const formatStr = use24Hour 
    ? 'HH:mm:ss' 
    : 'h:mm:ss a';
  
  const formatted = formatInTimeZone(date, tz, formatStr);
  
  return `${formatted} (${tzAbbr})`;
}

/**
 * Get timezone abbreviation
 * @param date - Date object
 * @param timezone - Timezone name
 * @returns Timezone abbreviation (e.g., "CST", "PST", etc.)
 */
function getTimezoneAbbreviation(date: Date, timezone: string): string {
  // Use Intl.DateTimeFormat to get the timezone name
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short'
  });
  
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find(part => part.type === 'timeZoneName');
  
  return tzPart?.value || timezone;
}

/**
 * Format date only
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string (e.g., "Jul 19, 2025")
 */
export function formatDate(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
  return format(date, 'MMM d, yyyy');
}

/**
 * Format date range
 * @param startDate - Start date ISO string or Date object
 * @param endDate - End date ISO string or Date object
 * @returns Formatted date range (e.g., "Jul 19, 2025 - Jul 26, 2025")
 */
export function formatDateRange(
  startDate: string | Date,
  endDate: string | Date
): string {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  
  // If same date, just return one
  if (start === end) {
    return start;
  }
  
  return `${start} - ${end}`;
}